import { Box, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';
import { PeopleSection } from '@verantos-dev/ui-annotation-tool';

import { useProjectOverview } from './useProjectOverview';
import { FeedbackSnackbar } from '../../../admin-common/components/FeedbackSnackbar';
// Called a second time here purely to read its loading flag for the page-level
// spinner aggregate below — RTK Query dedupes the underlying request via its cache.
import { useGetMandatoryAttributeNamesQuery } from '../../../create-project-wizard/api/createProjectApiSlice';
import { AllocationSummaryTable } from '../ProjectOverviewContent/AllocationSummaryTable';
import { DatasetSummary } from '../ProjectOverviewContent/DatasetSummary';
import { ProjectBasics } from '../ProjectOverviewContent/ProjectBasics';
import { ReviewConceptAndAttributes } from '../ProjectOverviewContent/ReviewConceptAndAttributes';
import { ReallocationWizard } from '../ReallocationWizard/ReallocationWizard';

export const ProjectOverview = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    projectName,
    conditionAreaName,
    healthSystemName,
    annotatorNames,
    supervisorNames,
    annotators,
    supervisors,
    addedConcepts,
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
    allocationSummary,
    isDataLoading,
    editMode,
    projectId,
    addedAnnotators,
    addedSupervisors,
    addAnnotator,
    removeAnnotator,
    addSupervisor,
    removeSupervisor,
    isReallocationWizardOpen,
    openReallocationWizard,
    closeReallocationWizard,
    handleConfirmReallocation,
    confirmedToast,
    closeConfirmedToast,
  } = useProjectOverview();
  const { isLoading: isMandatoryAttributeNamesLoading } = useGetMandatoryAttributeNamesQuery();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;
  const isLoading = isDataLoading || isMandatoryAttributeNamesLoading;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
      <Box>
        <Typography variant="h5">
          {formatMessage({ id: 'adminProjects.projectOverview.title' })}
        </Typography>
      </Box>
      {/* <Alert severity="info" sx={{ mt: 2 , border: `1px solid ${theme.palette.info.main}`}}>
                {formatMessage({ id: 'adminProjects.projectOverview.alert.description' })}
            </Alert> */}
      <Box>
        <ProjectBasics
          projectName={projectName}
          conditionAreaName={conditionAreaName}
          healthSystemName={healthSystemName}
        />
      </Box>
      {showLoadingSpinner && isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <LoadingSpinner id="projectOverviewLoading" />
        </Box>
      ) : (
        <>
          <Box>
            <PeopleSection
              annotatorNames={annotatorNames}
              supervisorNames={supervisorNames}
              editable={editMode}
              persistedAnnotators={annotators}
              persistedSupervisors={supervisors}
              addedAnnotators={addedAnnotators}
              addedSupervisors={addedSupervisors}
              onAddAnnotator={addAnnotator}
              onRemoveAnnotator={removeAnnotator}
              onAddSupervisor={addSupervisor}
              onRemoveSupervisor={removeSupervisor}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
            <Box sx={{ flexGrow: 1 }}>
              <ReviewConceptAndAttributes addedConcepts={addedConcepts} />
            </Box>

            <Box
              id="review-sidebar"
              sx={{
                width: '450px',
                p: theme.spacing(4),
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(4),
              }}
            >
              <DatasetSummary
                validationEncounterCount={validationEncounterCount}
                testEncounterCount={testEncounterCount}
                trainingEncounterCount={trainingEncounterCount}
              />
              <AllocationSummaryTable
                allocationSummary={allocationSummary}
                editable={editMode}
                onReallocate={openReallocationWizard}
              />
            </Box>
          </Box>
        </>
      )}
      <ReallocationWizard
        open={isReallocationWizardOpen}
        projectId={projectId}
        onClose={closeReallocationWizard}
        onConfirm={handleConfirmReallocation}
      />
      <FeedbackSnackbar
        open={Boolean(confirmedToast)}
        message={confirmedToast ?? ''}
        severity="info"
        onClose={closeConfirmedToast}
      />
    </Box>
  );
};
